import * as React from "react";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import Editor from "@monaco-editor/react";

import { monacoEditorRestriction, singleLineMonacoEditorRestriction, multiLineMonacoEditorRestriction } from "../lang/restrictions";
import { MonacoEditorProps } from "../model/model";
import { useEditor } from "../hooks/useEditor";
import styled from "styled-components";
import { getEditorSettings } from "../lang/settings";
import { ThemeType } from "shared/styles/theme";
import registerMonacoBaseThemes from "shared/monaco/theme";

registerMonacoBaseThemes();

const MonacoEditorContainer = styled.div`
    width: 100%;
    height: 100%;
`

const MonacoEditor: React.FC<MonacoEditorProps> = ({
    value,
    onChange,
    mode,
    onInstantChange,
    readOnly,
    wordWrap,
    height,
    theme,
    hideMinimap,
    decorations,
    cursorStartingPosition,
    onEditorContentSizeChange,
    onEditorFocusChange,
    onDidChangeCursorPosition
}) => {
    const editorRef = React.useRef<monaco.editor.IStandaloneCodeEditor>();
    const monacoRef = React.useRef<typeof monaco>();
    const language = useEditor({ mode, editorRef, monacoRef, wordWrap });

    const [editorHeight, setEditorHeight] = React.useState<number | string>(height ?? 19) // Initialize editor as single line by default
    const updateEditorOptions = (editor: monaco.editor.IStandaloneCodeEditor) => {
        // Toggle between restrictions based on linecount
        const shouldHaveSingleEditorRestrictions = editor.getModel()?.getLineCount() === 1;
        if (shouldHaveSingleEditorRestrictions) {
            editor.updateOptions({ ...singleLineMonacoEditorRestriction });
        } else {
            editor.updateOptions({ ...multiLineMonacoEditorRestriction, minimap: { enabled: !(hideMinimap === true) } })
        }
        setEditorHeight(Math.min(1000, editor.getContentHeight()));
        editor.layout();
    }

    const handleOnMount = (editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: typeof monaco) => {
        getEditorSettings(editor);
        // Assume we're dealing with a multiline editor if height prop has been passed, otherwise set height from content.
        if (height === undefined) {
            updateEditorOptions(editor);
            editor.onDidContentSizeChange(() => {
                updateEditorOptions(editor);
            });
        } else {
            editor.updateOptions({ ...multiLineMonacoEditorRestriction })
        }
        if (readOnly) {
            editor.updateOptions({ readOnly: true });
        }

        if (cursorStartingPosition) {
            editor.setPosition(cursorStartingPosition);
        }

        if (onEditorContentSizeChange) {
            editor.onDidContentSizeChange(onEditorContentSizeChange);
        }

        if (onEditorFocusChange) {
            editor.onDidFocusEditorWidget(() => onEditorFocusChange(true));
            editor.onDidBlurEditorWidget(() => onEditorFocusChange(false));
        }

        editorRef.current = editor;
        monacoRef.current = monacoInstance;
    }


    const handleEditorChange = () => {
        if (onChange !== undefined) {
            onChange(editorRef.current?.getValue());
        }
    };

    const handleEditorInstantChange = () => {
        if (onInstantChange) {
            onInstantChange(editorRef.current?.getValue())
        }
    }

    React.useEffect(() => {
        if (decorations && editorRef.current) {
            editorRef.current.createDecorationsCollection(decorations);
        }
    }, [decorations]);

    React.useEffect(() => {
        if (onDidChangeCursorPosition && editorRef.current) {
            editorRef.current.onDidChangeCursorPosition((event) => onDidChangeCursorPosition(event, editorRef.current!));
        }
    }, [onDidChangeCursorPosition]);

    const getTheme = (): "frends-light" | "frends-dark" | "frendsTheme" => {
        // Fallback to our base colors if the editor doesn't have the LanguageProvider or is a read-only editor
        if (theme !== undefined || readOnly) {
            if (theme === ThemeType.light) {
                return "frends-light";
            }
            return "frends-dark";
        }
        return "frendsTheme";
    }

    return (
        <MonacoEditorContainer onBlur={handleEditorChange} className="monaco-editor-selector">
            <Editor
                value={value}
                height={editorHeight}
                options={monacoEditorRestriction}
                onMount={handleOnMount}
                theme={getTheme()}
                language={language}
                onChange={handleEditorInstantChange}
            />
        </MonacoEditorContainer>
    )
}

export default MonacoEditor;